import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'helpers/api';
import moment from 'moment';
import PropTypes from 'prop-types';
import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import Select from 'react-select';

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('accessToken')}`
});

const asOptionLabel = prescription => {
  const drug = [
    prescription.drug,
    prescription.drug_name,
    prescription.strength_value,
    prescription.strength,
    prescription.drug_strength
  ]
    .filter(Boolean)
    .join(' ');

  return drug || `Prescription #${prescription.id}`;
};

const parseSlotTimes = value => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value !== 'string') return [];
  return value
    .split(',')
    .map(slot => slot.trim())
    .filter(Boolean);
};

const getVitalIcon = vital => {
  const iconMap = {
    BP: 'stethoscope',
    Temp: 'procedures',
    Pulse: 'user-md',
    Weight: 'hospital-user',
    Resp: 'head-side-cough',
    BS: 'tint',
    O2: 'stethoscope',
    Pain: 'user-injured'
  };

  return iconMap[vital.short_name] || 'stethoscope';
};

const thresholdValue = value =>
  value === null || value === undefined ? '' : value;
const payloadThresholdValue = value => (value === '' ? undefined : value);

const getDefaultThresholds = () => ({
  threshold_min: '',
  threshold_max: '',
  threshold_min_2: '',
  threshold_max_2: ''
});

const defaultAssignment = vital => ({
  vital_sign_id: vital.id,
  is_required: 1,
  is_active: 0,
  prescription_ids: [],
  slot_times: [],
  ...getDefaultThresholds(),
  linked_prescriptions: []
});

const normalizeAssignment = (vital, assignment) => ({
  vital_sign_id: Number(vital.id),
  is_required: Number(assignment?.is_required ?? 1),
  is_active: Number(assignment?.is_active ?? 0),
  prescription_ids: [...(assignment?.prescription_ids || [])]
    .map(Number)
    .filter(Boolean)
    .sort((a, b) => a - b),
  slot_times: [...(assignment?.slot_times || [])].filter(Boolean).sort(),
  threshold_min: thresholdValue(assignment?.threshold_min),
  threshold_max: thresholdValue(assignment?.threshold_max),
  threshold_min_2: thresholdValue(assignment?.threshold_min_2),
  threshold_max_2: thresholdValue(assignment?.threshold_max_2)
});

const assignmentsEqual = (vital, currentAssignment, savedAssignment) =>
  JSON.stringify(normalizeAssignment(vital, currentAssignment)) ===
  JSON.stringify(normalizeAssignment(vital, savedAssignment));

const hourlyTimeOptions = Array.from({ length: 24 }, (_, hour) => {
  const value = `${String(hour).padStart(2, '0')}:00`;
  return { value, label: moment(value, 'HH:mm').format('h:mm A') };
});

const PatientVitals = ({ patientid, data }) => {
  const [masterVitals, setMasterVitals] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [savedAssignments, setSavedAssignments] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingAssignments, setSavingAssignments] = useState(false);

  const activePrescriptions = useMemo(
    () =>
      (data?.prescriptions || [])
        .filter(prescription => prescription.discontinued_date == null)
        .sort((a, b) =>
          asOptionLabel(a).localeCompare(asOptionLabel(b), 'en', {
            sensitivity: 'base'
          })
        ),
    [data]
  );

  const prescriptionOptions = useMemo(
    () =>
      activePrescriptions.map(prescription => ({
        value: Number(prescription.id),
        label: asOptionLabel(prescription)
      })),
    [activePrescriptions]
  );

  const activeAssignmentCount = useMemo(
    () =>
      Object.values(assignments).filter(
        assignment => Number(assignment.is_active) === 1
      ).length,
    [assignments]
  );

  const slotAssignmentCount = useMemo(
    () =>
      Object.values(assignments).filter(
        assignment =>
          Number(assignment.is_active) === 1 &&
          (assignment.prescription_ids || []).length === 0 &&
          (assignment.slot_times || []).length > 0
      ).length,
    [assignments]
  );

  const prescriptionAssignmentCount = useMemo(
    () =>
      Object.values(assignments).filter(
        assignment =>
          Number(assignment.is_active) === 1 &&
          (assignment.prescription_ids || []).length > 0
      ).length,
    [assignments]
  );

  const fetchMasterVitals = useCallback(async () => {
    const response = await axios.post(
      `${process.env.REACT_APP_SERVER_URL}/prescriptions/fetch-multiple`,
      { vitals: {} },
      { headers: authHeaders() }
    );

    return response?.data?.output?.data?.vitals || [];
  }, []);

  const fetchAssignments = useCallback(async () => {
    const response = await axios.get(
      `${process.env.REACT_APP_SERVER_URL}/patients/patient-vital-signs/${patientid}`,
      { headers: authHeaders() }
    );

    return {
      assigned: response?.data?.data || [],
      prescriptionOnly: response?.data?.prescription_only || []
    };
  }, [patientid]);

  const getLinkedPrescriptionIds = item => {
    const ids = (item.linked_prescriptions || []).map(link =>
      Number(link.prescription_id)
    );

    if (item.prescription_id) ids.push(Number(item.prescription_id));

    return [...new Set(ids.filter(Boolean))];
  };

  const mapAssignmentRows = assignmentRows =>
    assignmentRows.reduce((acc, item) => {
      const prescriptionIds = getLinkedPrescriptionIds(item);
      const slotTimes = parseSlotTimes(item.slot_times);
      const linkedPrescriptions = item.linked_prescriptions || [];
      const linkWithRanges = linkedPrescriptions.find(
        p =>
          p.threshold_min ||
          p.threshold_max ||
          p.threshold_min_2 ||
          p.threshold_max_2
      );

      acc[item.vital_sign_id] = {
        vital_sign_id: item.vital_sign_id,
        is_required: Number(item.is_required ?? 1),
        is_active: Number(item.is_active ?? 1),
        prescription_ids: prescriptionIds,
        slot_times:
          prescriptionIds.length > 0
            ? []
            : slotTimes.length > 0
            ? slotTimes
            : parseSlotTimes(item.vital_time || item.record_time || item.time),
        threshold_min: thresholdValue(
          item.threshold_min ?? linkWithRanges?.threshold_min
        ),
        threshold_max: thresholdValue(
          item.threshold_max ?? linkWithRanges?.threshold_max
        ),
        threshold_min_2: thresholdValue(
          item.threshold_min_2 ?? linkWithRanges?.threshold_min_2
        ),
        threshold_max_2: thresholdValue(
          item.threshold_max_2 ?? linkWithRanges?.threshold_max_2
        ),
        linked_prescriptions: linkedPrescriptions
      };

      return acc;
    }, {});

  const hydrateAssignments = useCallback((masterList, configuredList = []) => {
    const configured = Array.isArray(configuredList)
      ? mapAssignmentRows(configuredList)
      : { ...configuredList };

    masterList.forEach(vital => {
      if (!configured[vital.id]) {
        configured[vital.id] = defaultAssignment(vital);
      } else {
        const linkedPrescriptions =
          configured[vital.id]?.linked_prescriptions || [];
        const linkWithRanges = linkedPrescriptions.find(
          p =>
            p.threshold_min ||
            p.threshold_max ||
            p.threshold_min_2 ||
            p.threshold_max_2
        );

        configured[vital.id] = {
          ...configured[vital.id],
          threshold_min: thresholdValue(
            configured[vital.id].threshold_min !== ''
              ? configured[vital.id].threshold_min
              : linkWithRanges?.threshold_min
          ),
          threshold_max: thresholdValue(
            configured[vital.id].threshold_max !== ''
              ? configured[vital.id].threshold_max
              : linkWithRanges?.threshold_max
          ),
          threshold_min_2: thresholdValue(
            configured[vital.id].threshold_min_2 !== ''
              ? configured[vital.id].threshold_min_2
              : linkWithRanges?.threshold_min_2
          ),
          threshold_max_2: thresholdValue(
            configured[vital.id].threshold_max_2 !== ''
              ? configured[vital.id].threshold_max_2
              : linkWithRanges?.threshold_max_2
          )
        };
      }
    });

    setAssignments(configured);
    setSavedAssignments(configured);
  }, []);

  const enableVitalsFromPrescriptionEvidence = (
    currentAssignments,
    masterList,
    prescriptionOnlyList = [],
    historyRows = []
  ) => {
    const nextAssignments = Array.isArray(currentAssignments)
      ? mapAssignmentRows(currentAssignments)
      : { ...currentAssignments };
    const masterById = masterList.reduce((acc, vital) => {
      acc[Number(vital.id)] = vital;
      return acc;
    }, {});

    const enablePrescriptionVital = (vitalId, sourceItem = {}) => {
      const id = Number(vitalId);
      const master = masterById[id];
      if (!master) return;

      const existing = nextAssignments[id] || defaultAssignment(master);
      const prescriptionIds = [
        ...new Set([
          ...(existing.prescription_ids || []),
          ...getLinkedPrescriptionIds(sourceItem)
        ])
      ];

      if (prescriptionIds.length === 0) return;

      const linkedPrescriptions = [
        ...(sourceItem.linked_prescriptions || []),
        ...(existing.linked_prescriptions || [])
      ];
      const linkWithRanges = linkedPrescriptions.find(
        p =>
          p.threshold_min ||
          p.threshold_max ||
          p.threshold_min_2 ||
          p.threshold_max_2
      );

      nextAssignments[id] = {
        ...existing,
        is_active: 1,
        is_required: Number(
          existing.is_required ?? sourceItem.is_required ?? 1
        ),
        prescription_ids: prescriptionIds,
        slot_times: [],
        threshold_min: thresholdValue(
          sourceItem.threshold_min ||
            linkWithRanges?.threshold_min ||
            existing.threshold_min
        ),
        threshold_max: thresholdValue(
          sourceItem.threshold_max ||
            linkWithRanges?.threshold_max ||
            existing.threshold_max
        ),
        threshold_min_2: thresholdValue(
          sourceItem.threshold_min_2 ||
            linkWithRanges?.threshold_min_2 ||
            existing.threshold_min_2
        ),
        threshold_max_2: thresholdValue(
          sourceItem.threshold_max_2 ||
            linkWithRanges?.threshold_max_2 ||
            existing.threshold_max_2
        )
      };
    };

    prescriptionOnlyList.forEach(item => {
      enablePrescriptionVital(item.vital_sign_id, item);
    });

    historyRows
      .filter(row => row.source === 'medpass' || row.prescription_id)
      .forEach(row => {
        enablePrescriptionVital(row.vital_sign_id, row);
      });

    return nextAssignments;
  };

  const refreshAll = async () => {
    setLoading(true);
    try {
      const [masterList, assignmentResponse] = await Promise.all([
        fetchMasterVitals(),
        fetchAssignments()
      ]);

      const sortedMaster = masterList.sort(
        (a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0)
      );
      setMasterVitals(sortedMaster);
      hydrateAssignments(
        sortedMaster,
        enableVitalsFromPrescriptionEvidence(
          assignmentResponse.assigned,
          sortedMaster,
          assignmentResponse.prescriptionOnly,
          []
        )
      );
    } catch (error) {
      toast.error('Error fetching patient vitals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshAll();
  }, [patientid]);

  const handleAssignmentChange = (vitalId, field, value) => {
    setAssignments(prev => ({
      ...prev,
      [vitalId]: {
        ...prev[vitalId],
        [field]: value
      }
    }));
  };

  const handlePrescriptionSelect = (vitalId, selectedOptions) => {
    const selectedIds = (selectedOptions || []).map(option =>
      Number(option.value)
    );

    setAssignments(prev => {
      const current = prev[vitalId] || {};
      const linkedPrescriptions = current.linked_prescriptions || [];
      const linkWithRanges = linkedPrescriptions.find(
        p =>
          selectedIds.includes(Number(p.prescription_id)) &&
          (p.threshold_min ||
            p.threshold_max ||
            p.threshold_min_2 ||
            p.threshold_max_2)
      );

      return {
        ...prev,
        [vitalId]: {
          ...current,
          prescription_ids: selectedIds,
          slot_times: selectedIds.length > 0 ? [] : current?.slot_times || [],
          threshold_min: thresholdValue(
            current.threshold_min !== ''
              ? current.threshold_min
              : linkWithRanges?.threshold_min
          ),
          threshold_max: thresholdValue(
            current.threshold_max !== ''
              ? current.threshold_max
              : linkWithRanges?.threshold_max
          ),
          threshold_min_2: thresholdValue(
            current.threshold_min_2 !== ''
              ? current.threshold_min_2
              : linkWithRanges?.threshold_min_2
          ),
          threshold_max_2: thresholdValue(
            current.threshold_max_2 !== ''
              ? current.threshold_max_2
              : linkWithRanges?.threshold_max_2
          )
        }
      };
    });
  };

  const handleSlotTimeChange = (vitalId, selectedOptions) => {
    const selectedTimes = (selectedOptions || []).map(option => option.value);

    setAssignments(prev => ({
      ...prev,
      [vitalId]: {
        ...prev[vitalId],
        slot_times: selectedTimes,
        prescription_ids:
          selectedTimes.length > 0 ? [] : prev[vitalId]?.prescription_ids || []
      }
    }));
  };

  const validateAssignments = () => {
    const invalid = masterVitals.find(vital => {
      const assignment = assignments[vital.id] || defaultAssignment(vital);
      if (Number(assignment.is_active) !== 1) return false;
      const hasPrescriptions = (assignment.prescription_ids || []).length > 0;
      const hasSlots = (assignment.slot_times || []).length > 0;
      return hasPrescriptions === hasSlots;
    });

    if (invalid) {
      toast.info(
        `${invalid.name} needs either linked prescriptions or a daily slot time.`
      );
      return false;
    }

    return true;
  };

  const saveAssignments = async () => {
    if (!validateAssignments()) return;

    setSavingAssignments(true);
    try {
      const payloads = masterVitals
        .filter(vital => {
          const assignment = assignments[vital.id] || defaultAssignment(vital);
          const savedAssignment =
            savedAssignments[vital.id] || defaultAssignment(vital);

          return !assignmentsEqual(vital, assignment, savedAssignment);
        })
        .map(vital => {
          const assignment = assignments[vital.id] || defaultAssignment(vital);
          const basePayload = {
            patient_id: patientid,
            vital_sign_id: vital.id,
            is_required: Number(assignment.is_required ?? 1),
            is_active: Number(assignment.is_active ?? 0)
          };

          if (Number(assignment.is_active) !== 1) return basePayload;

          const hasSlots = (assignment.slot_times || []).length > 0;
          const activePayload = {
            ...basePayload,
            ...(hasSlots
              ? {}
              : {
                  threshold_min: payloadThresholdValue(
                    assignment.threshold_min
                  ),
                  threshold_max: payloadThresholdValue(
                    assignment.threshold_max
                  ),
                  threshold_min_2: payloadThresholdValue(
                    assignment.threshold_min_2
                  ),
                  threshold_max_2: payloadThresholdValue(
                    assignment.threshold_max_2
                  )
                })
          };

          if ((assignment.prescription_ids || []).length > 0) {
            return {
              ...activePayload,
              prescription_ids: assignment.prescription_ids,
              slot_times: []
            };
          }

          return {
            ...activePayload,
            prescription_ids: [],
            slot_times: assignment.slot_times || []
          };
        });

      if (payloads.length === 0) {
        toast.info('No vital changes to save');
        return;
      }

      const responses = await Promise.all(
        payloads.map(payload =>
          axios.post(
            `${process.env.REACT_APP_SERVER_URL}/patients/set-patient-vital-signs`,
            payload,
            { headers: authHeaders() }
          )
        )
      );

      const failed = responses.find(response => response?.data?.status === false);
      if (failed) {
        toast.info(
          failed?.data?.message || 'Unable to update one or more vitals'
        );
      } else {
        toast.success('Patient vitals updated successfully');
        await refreshAll();
        window.dispatchEvent(
          new CustomEvent('patientVitalsUpdated', {
            detail: { patientid }
          })
        );
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Error updating patient vitals'
      );
    } finally {
      setSavingAssignments(false);
    }
  };

  if (loading) {
    return (
      <div className="py-5 text-center">
        <Spinner animation="border" size="sm" className="me-2" />
        Loading patient vitals...
      </div>
    );
  }

  return (
    <div className="patient-vitals patient-vitals-v2">
      <div className="vitals-command-bar">
        <div>
          {/* <div className="text-uppercase fw-bold fs-11 text-primary mb-1">
            Phase 2 Vitals
          </div> */}
          <h5 className="mb-1">Daily slots, prescription links, trends</h5>
          <div className="text-600 fs-10">
            Patient-level vitals use a slot time. Prescription-linked vitals use
            medpass timing.
          </div>
        </div>
        <div className="vitals-metric-strip">
          <div>
            <span>{activeAssignmentCount}</span>
            <small>Active</small>
          </div>
          <div>
            <span>{slotAssignmentCount}</span>
            <small>Time Slots</small>
          </div>
          <div>
            <span>{prescriptionAssignmentCount}</span>
            <small>Prescription linked</small>
          </div>
        </div>
      </div>

      <Row className="g-3 align-items-stretch">
        <Col>
          <Card className="vitals-panel h-100">
            <Card.Body className="p-0">
              {masterVitals.map((vital, index) => {
                const assignment =
                  assignments[vital.id] || defaultAssignment(vital);
                const isActive = Number(assignment.is_active) === 1;
                const linkedPrescriptionIds = assignment.prescription_ids || [];
                const selectedPrescriptionOptions = prescriptionOptions.filter(
                  option => linkedPrescriptionIds.includes(Number(option.value))
                );
                const selectedSlotTimes = assignment.slot_times || [];
                const selectedSlotOptions = hourlyTimeOptions.filter(option =>
                  selectedSlotTimes.includes(option.value)
                );
                const hasSlotTimes = selectedSlotTimes.length > 0;
                const mode =
                  linkedPrescriptionIds.length > 0
                    ? 'prescription'
                    : selectedSlotTimes.length > 0
                    ? 'slot'
                    : 'unset';

                const unitLabel = vital.unit ? ` ${vital.unit}` : '';

                return (
                  <div
                    key={vital.id}
                    className={`vital-assignment-row ${
                      isActive ? 'is-active' : ''
                    } ${index !== masterVitals.length - 1 ? 'border-bottom' : ''}`}
                  >
                    <div className="d-flex justify-content-between align-items-start gap-3">
                      <div className="d-flex align-items-center gap-3 min-w-0">
                        <span className="vital-assignment-icon">
                          <FontAwesomeIcon icon={getVitalIcon(vital)} />
                        </span>
                        <div className="min-w-0">
                          <div className="d-flex flex-wrap align-items-center gap-2">
                            <div className="fw-semibold text-900">
                              {vital.name}
                            </div>
                            {/* <Badge
                              bg={
                                mode === 'slot'
                                  ? 'success'
                                  : mode === 'prescription'
                                  ? 'primary'
                                  : 'secondary'
                              }
                              className="rounded-pill"
                            >
                              {mode === 'slot'
                                ? 'Daily slot'
                                : mode === 'prescription'
                                ? 'Medpass'
                                : 'Needs timing'}
                            </Badge> */}
                          </div>
                        </div>
                      </div>
                      <Form.Check
                        type="switch"
                        checked={isActive}
                        onChange={event =>
                          handleAssignmentChange(
                            vital.id,
                            'is_active',
                            event.target.checked ? 1 : 0
                          )
                        }
                      />
                    </div>

                    {isActive && (
                      <Row className="g-3 mt-1">
                        <Col lg={7}>
                          <Form.Label className="fs-11 fw-semibold text-700 mb-1">
                            Prescription
                            <span className="text-500 fw-normal ms-2">
                              {linkedPrescriptionIds.length} selected
                            </span>
                          </Form.Label>
                          <Select
                            isMulti
                            options={prescriptionOptions}
                            placeholder="Select prescriptions"
                            value={selectedPrescriptionOptions}
                            onChange={selectedOptions =>
                              handlePrescriptionSelect(vital.id, selectedOptions)
                            }
                            classNamePrefix="react-select"
                          />
                        </Col>
                        <Col lg={5}>
                          <Form.Group>
                            <Form.Label className="fs-11 fw-semibold text-700 mb-1">
                              Time slot
                              {linkedPrescriptionIds.length > 0 && (
                                <span className="text-500 fw-normal ms-2">
                                  Cleared while prescriptions are linked
                                </span>
                              )}
                            </Form.Label>
                            <Select
                              isMulti
                              options={hourlyTimeOptions}
                              placeholder="Select slot times"
                              value={selectedSlotOptions}
                              isDisabled={linkedPrescriptionIds.length > 0}
                              onChange={selectedOptions =>
                                handleSlotTimeChange(vital.id, selectedOptions)
                              }
                              classNamePrefix="react-select"
                            />
                          </Form.Group>
                        </Col>
                        {vital.data_type !== 'text' && !hasSlotTimes  && linkedPrescriptionIds.length > 0  && (
                          vital.data_type === 'dual' ? (
                            <>
                              <Col lg={6} className="mt-2">
                                <Row className="g-2">
                                  <Col xs={6}>
                                    <Form.Group>
                                      <Form.Label className="fs-11 fw-semibold text-700 mb-1">
                                        Systolic Max ({unitLabel})
                                      </Form.Label>
                                      <Form.Control
                                        type="number"
                                        size="sm"
                                        min="0"
                                        value={assignment.threshold_max}
                                        onChange={e => handleAssignmentChange(vital.id, 'threshold_max', e.target.value)}
                                      />
                                    </Form.Group>
                                  </Col>
                                  <Col xs={6}>
                                    <Form.Group>
                                      <Form.Label className="fs-11 fw-semibold text-700 mb-1">
                                        Systolic Min ({unitLabel})
                                      </Form.Label>
                                      <Form.Control
                                        type="number"
                                        size="sm"
                                        min="0"
                                        value={assignment.threshold_min}
                                        onChange={e => handleAssignmentChange(vital.id, 'threshold_min', e.target.value)}
                                      />
                                    </Form.Group>
                                  </Col>
                                </Row>
                              </Col>
                              <Col lg={6} className="mt-2">
                                <Row className="g-2">
                                  <Col xs={6}>
                                    <Form.Group>
                                      <Form.Label className="fs-11 fw-semibold text-700 mb-1">
                                        Diastolic Max ({unitLabel})
                                      </Form.Label>
                                      <Form.Control
                                        type="number"
                                        size="sm"
                                        min="0"
                                        value={assignment.threshold_max_2}
                                        onChange={e => handleAssignmentChange(vital.id, 'threshold_max_2', e.target.value)}
                                      />
                                    </Form.Group>
                                  </Col>
                                  <Col xs={6}>
                                    <Form.Group>
                                      <Form.Label className="fs-11 fw-semibold text-700 mb-1">
                                        Diastolic Min ({unitLabel})
                                      </Form.Label>
                                      <Form.Control
                                        type="number"
                                        size="sm"
                                        min="0"
                                        value={assignment.threshold_min_2}
                                        onChange={e => handleAssignmentChange(vital.id, 'threshold_min_2', e.target.value)}
                                      />
                                    </Form.Group>
                                  </Col>
                                </Row>
                              </Col>
                            </>
                          ) : (
                            <>
                            <Col lg={6} xs={6} className="mt-2">
                                <Form.Group>
                                  <Form.Label className="fs-11 fw-semibold text-700 mb-1">
                                    Max ({unitLabel})
                                  </Form.Label>
                                  <Form.Control
                                    type="number"
                                    size="sm"
                                    min="0"
                                    value={assignment.threshold_max}
                                    onChange={e => handleAssignmentChange(vital.id, 'threshold_max', e.target.value)}
                                  />
                                </Form.Group>
                              </Col>
                              <Col lg={6} xs={6} className="mt-2">
                                <Form.Group>
                                  <Form.Label className="fs-11 fw-semibold text-700 mb-1">
                                    Min ({unitLabel})
                                  </Form.Label>
                                  <Form.Control
                                    type="number"
                                    size="sm"
                                    min="0"
                                    value={assignment.threshold_min}
                                    onChange={e => handleAssignmentChange(vital.id, 'threshold_min', e.target.value)}
                                  />
                                </Form.Group>
                              </Col>
                            </>
                          )
                        )}
                      </Row>
                    )}
                  </div>
                );
              })}
            </Card.Body>
            <Card.Footer className="d-flex justify-content-end align-items-center gap-2">
              <Button
                size="sm"
                onClick={saveAssignments}
                disabled={savingAssignments}
              >
                {savingAssignments ? 'Saving...' : 'Save Vitals'}
              </Button>
            </Card.Footer>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

PatientVitals.propTypes = {
  patientid: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  data: PropTypes.shape({
    prescriptions: PropTypes.array
  })
};

PatientVitals.defaultProps = {
  data: {
    prescriptions: []
  }
};

export default PatientVitals;
